import type { ReactNode } from 'react';
import { Loading, Skeleton } from '@/components/Skeleton';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ONE REPORT SECTION'S SHELL, AND ITS THREE STATES. [ANALYTICS §5; promoted P7C §2]
 *
 *  ⚠️ PROMOTED ON ITS SECOND USER, `DataFigures`' precedent. Usage analytics' `AnalyticsSection` was
 *  this file; Duty analytics' sections fail the same way (`{ data } | { failure }`) and need the same
 *  card, so the shell moved here and `AnalyticsSection` became a one-line wrapper. ⚠️ WITH NO
 *  `actions` THE DOM IS BYTE-IDENTICAL to what it was — the `<h2>` stays the section's direct child —
 *  and the Usage analytics suite is the proof, re-run unchanged.
 *
 *  ⚠️ A FAILED SECTION SAYS SO, AND THE OTHERS STILL RENDER. The wire makes every section
 *  `{ data } | { failure: 'UNAVAILABLE' }` precisely so one that could not be computed does not blank
 *  the page — and never arrives as zeros, which would claim nothing happened. The sentence is the
 *  whole of the failure: no retry per section, because each report is ONE request and a per-section
 *  retry would reload every other section, a promise the network does not keep.
 *
 *  ⚠️ `undefined` IS LOADING, NOT A THIRD FAILURE — the page has no report yet, so the section shows
 *  its own skeleton inside a `Loading` that keeps a screen reader's announcement.
 *
 *  ⚠️ THE WORDS ARE PROPS. `components/` never imports a page's copy (`Skeleton`'s rule), so the
 *  loading label and the failure node come from the caller.
 *
 *  ⚠️ [P7C.1 §3] AN `aside` SITS BESIDE THE HEADING, OUTSIDE IT — inside the `<h2>` it would join the section's
 *  name. It is grouped with the heading so `actions` keeps the far end, and the header renders before the state
 *  switch, so an info button is reachable on a section that failed. With no `aside` the DOM is unchanged.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type SectionState<T> = { data: T } | { failure: 'UNAVAILABLE' } | undefined;

export const ReportSection = <T,>({
  id,
  testIdPrefix,
  title,
  state,
  loadingLabel,
  failure,
  actions,
  aside,
  children,
}: {
  id: string;
  /** The page's own namespace: `analytics` on Usage analytics, `duty-analytics` on Duty analytics. */
  testIdPrefix: string;
  title: string;
  state: SectionState<T>;
  loadingLabel: string;
  failure: ReactNode;
  /** A control beside the heading. Absent, the heading is the section's direct child, as it always was. */
  actions?: ReactNode;
  /** [P7C.1 §3] Something read WITH the heading — an info button — kept beside it, outside the `<h2>`. */
  aside?: ReactNode;
  children: (data: T) => ReactNode;
}) => {
  const heading = (
    <h2 id={`${testIdPrefix}-${id}`} className="text-base font-medium text-heading">
      {title}
    </h2>
  );
  const titled =
    aside === undefined ? (
      heading
    ) : (
      <div className="flex min-w-0 items-center gap-1.5">
        {heading}
        {aside}
      </div>
    );
  return (
    <section
      aria-labelledby={`${testIdPrefix}-${id}`}
      data-testid={`${testIdPrefix}-${id}`}
      className="flex flex-col gap-4 rounded-lg border border-border bg-card p-5"
    >
      {actions === undefined ? (
        titled
      ) : (
        <div className="flex flex-wrap items-start justify-between gap-3">
          {titled}
          {actions}
        </div>
      )}
      {state === undefined ? (
        <Loading label={loadingLabel}>
          <div className="flex flex-col gap-2">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </Loading>
      ) : 'failure' in state ? (
        failure
      ) : (
        children(state.data)
      )}
    </section>
  );
};
